import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Steps } from 'primereact/steps'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  API_APPRENTICESHIP_MODULE_GRADE_BULK_CREATE,
  API_APPRENTICESHIP_MODULE_PLAN,
  API_GROUP_MEMBERS_BY_GROUP_ID,
} from '../../../../constants'
import { ApprenticeModuleGrade } from '../../../../model/apprenticeModuleGrade.model'
import {
  ApprenticeshipModuleInPlan,
  ApprenticeshipModulePlan,
} from '../../../../model/apprenticeshipModule.model'
import { RequestMethod } from '../../../../model/enums/enums'
import { GroupMember } from '../../../../model/group.model'
import { Apprentice, Person } from '../../../../model/person.model'
import { State } from '../../../../model/state.model'
import { useAppSelector } from '../../../../redux/hooks'
import { AuthRoutes } from '../../../../router/routes'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import { AvatarWithName } from '../../../util/AvatarWithName'
import { BackButton } from '../../../util/button/BackButton'
import { actionBodyRemove } from '../../../util/data-table/cell-body/ActionBody'

import { ModulePlanDropdown } from '../module/utils/ModulePlanDropdown'

interface ModuleInPlan {
  apprenticeship_module_id: number
  id_apprenticeship_module_type: number
  semester: number
  id_rounding_type: number
  id_grade_schema: number
  id_apprentice?: number
}

interface CreateGradeContainersBody {
  apprenticeship_modules_in_plan: ModuleInPlan[]
  apprentice_ids: number[]
}

export const CreateApprenticeGradeContainers = () => {
  const { loadedBasicInformation } = useAppSelector((state) => state.user)
  const apprentices = useAppSelector((state) => state.apprentices)

  const navigate = useNavigate()
  const [modulePlan, setModulePlan] = useState<ApprenticeshipModulePlan>()
  const [selectedModules, setSelectedModules] = useState<
    ApprenticeshipModuleInPlan[]
  >([])

  const [selectedApprentices, setSelectedApprentices] = useState<Apprentice[]>(
    []
  )

  const professions = useAppSelector((state) => state.professions)

  const [createdModules, setCreatedModules] = useState<ApprenticeModuleGrade[]>(
    []
  )
  const [existingModules, setExistingModules] = useState<
    ApprenticeModuleGrade[]
  >([])

  const setApprenticesFromGroup = async (mp: ApprenticeshipModulePlan) => {
    if (!mp || apprentices.length === 0) {
      console.warn(
        '[Create Apprentice Grade Container] Module plan not defined or apprentices not loaded.'
      )
      return
    }
    const groups = mp.groups
    let groupMembers: number[] = []
    const apiRequests: Promise<State<GroupMember[]>>[] = []
    groups?.forEach((group) => {
      const prom = handleRequest<GroupMember[]>(
        API_GROUP_MEMBERS_BY_GROUP_ID(group.group_id),
        RequestMethod.GET
      )
      apiRequests.push(prom)
    })
    Promise.all(apiRequests).then((result) => {
      result.forEach((memberArray) => {
        if (memberArray?.data && !memberArray?.error) {
          memberArray.data.forEach((member: GroupMember) => {
            groupMembers.push(member.id_person)
          })
        }
      })
      const tempApps: Apprentice[] = apprentices.filter(
        (apprentice: Apprentice) => {
          if (apprentice.person_id) {
            return groupMembers.includes(apprentice.person_id)
          }
        }
      )

      // remove duplicates in case they are in multiple groups
      setSelectedApprentices([...new Set(tempApps)])
    })
  }

  const loadPlan = async (id: number): Promise<void> => {
    const result = await handleRequest<ApprenticeshipModulePlan>(
      API_APPRENTICESHIP_MODULE_PLAN(id),
      RequestMethod.GET
    )

    if (result.data && !result.error) {
      setModulePlan(result.data)
      if (result.data.apprenticeship_modules) {
        setSelectedModules(result.data.apprenticeship_modules)
      }
      await setApprenticesFromGroup(result.data)
    } else {
      console.warn(
        '[Create Apprentice Grade Containers] Error loading the education plan.'
      )
    }
  }

  const location = useLocation()
  useEffect(() => {
    if (location && loadedBasicInformation) {
      // the location is at this point ...Company/<companyId> and we want to extract the Company id.
      // Reason: If someone sends this link we want to display the data of this url
      const n = location.pathname.lastIndexOf('/')
      const eduPlanId = parseInt(location.pathname.substring(n + 1))

      if (!eduPlanId) {
        console.info(
          '[Create Apprentice Grade Containers] Education-Plan-ID not defined, opening without selecting.'
        )
        navigate(AuthRoutes.assignGradeContainer)
        return
      }
      loadPlan(eduPlanId)
    }
  }, [loadedBasicInformation])

  const removeModule = (m: ApprenticeshipModuleInPlan) => {
    const temp = selectedModules.filter(
      (x) =>
        x.apprenticeship_module_id !== m.apprenticeship_module_id ||
        (x.apprenticeship_module_id === m.apprenticeship_module_id &&
          x.semester !== m.semester)
    )
    setSelectedModules(temp)
  }

  const removeApprentice = (a: Apprentice) => {
    const temp = selectedApprentices.filter((x) => x.person_id !== a.person_id)
    setSelectedApprentices(temp)
  }

  const clearForm = () => {
    setShowFeedback(false)
    setExistingModules([])
    setCreatedModules([])
    setSelectedModules([])
    setSelectedApprentices([])
  }

  const hasApprenticeObj = (
    obj: Apprentice
  ): obj is { apprentice: { apprentice_id: number } } => {
    return obj.apprentice !== undefined
  }

  const [showFeedback, setShowFeedback] = useState<boolean>(false)

  const stepItems = [
    { label: t<string>('core.preview') },
    { label: t<string>('core.review_result') },
  ]

  const handleCreate = async () => {
    setShowFeedback(false)
    if (!modulePlan) {
      console.warn('Module plan not selected, cannot create')
      return
    }

    const apprenticeIds: number[] = selectedApprentices
      .filter(hasApprenticeObj)
      .map((a) => a.apprentice!.apprentice_id)

    const body: CreateGradeContainersBody = {
      apprentice_ids: apprenticeIds,
      apprenticeship_modules_in_plan: selectedModules.map((sm) => {
        return {
          apprenticeship_module_id: sm.apprenticeship_module_id,
          id_apprenticeship_module_type: sm.id_apprenticeship_module_type,
          id_grade_schema: sm.id_grade_schema ? sm.id_grade_schema : 2, // choose swiss schema if it is missing
          id_rounding_type: sm.id_rounding_type,
          semester: sm.semester,
        }
      }),
    }

    //CreateGradeContainersBody
    const result = await handleRequest<{
      created: ApprenticeModuleGrade[]
      existing: ApprenticeModuleGrade[]
    }>(API_APPRENTICESHIP_MODULE_GRADE_BULK_CREATE(), RequestMethod.POST, body)

    if (!result.error) {
      console.info(
        '[Create Apprentice Grade Containers] Successfully created grade container.'
      )

      if (result?.data) {
        setCreatedModules(result.data.created)
        setExistingModules(result.data.existing)
      } else {
        setCreatedModules([])
        setExistingModules([])
      }
    } else {
      console.warn(
        '[Create Apprentice Grade Containers] Could not create grade container'
      )
      setCreatedModules([])
      setExistingModules([])
    }
    setShowFeedback(true)
  }

  const SpecialityString = (row: Apprentice) => {
    const specialityId = row?.apprentice?.id_speciality
    const professionId = row?.apprentice?.id_profession
    let specialityString = ''

    if (specialityId && professionId) {
      const temp = professions
        .find((p) => p.profession_id === professionId)
        ?.specialities?.find(
          (s) => s.speciality_id === specialityId
        )?.speciality_name
      if (temp) {
        specialityString = ` (${temp})`
      }
    }
    return specialityString
  }
  const ProfessionString = (row: Apprentice) => {
    const professionId = row?.apprentice?.id_profession
    let professionString = ''

    if (professionId) {
      const temp = professions.find(
        (p) => p.profession_id === professionId
      )?.profession
      if (temp) {
        professionString = ` (${temp})`
      }
    }
    return professionString
  }

  // this could be error-prone since it only returns the apprentice from the list but logic wise it shouldn't ever return another apprentice
  const getApprentice = (idApprentice: number | undefined) => {
    const foundAppr = selectedApprentices.find(
      (a) => a?.apprentice?.apprentice_id === idApprentice
    )
    return foundAppr ? foundAppr : ({} as Person)
  }

  return (
    <div className={'card'}>
      <BackButton
        route={AuthRoutes.educationPlans}
        translationKey={'navigation.go_back_overview'}
      />
      <div className={'flex-wrap mr-4'}>
        <label htmlFor={'modulePlanDropdown'}>
          {t<string>('grade_module.education_plan.name')}
        </label>

        <ModulePlanDropdown
          selected={modulePlan}
          setSelected={(mp: ApprenticeshipModulePlan) => {
            clearForm()
            setModulePlan(mp)
            if (mp.apprenticeship_modules) {
              mp.apprenticeship_modules.map((am) => {
                const amip = am
                amip.id_grade_schema = am.id_grade_schema
                  ? am.id_grade_schema
                  : 2

                // if it is not passed/not passed we set rounding to 0.5 up
                if (am.id_grade_schema !== 5) {
                  amip.id_rounding_type = am.id_rounding_type
                    ? am.id_rounding_type
                    : 3
                }

                return amip
              })
              console.log(JSON.stringify(mp.apprenticeship_modules))
              setSelectedModules(mp.apprenticeship_modules)
            }
            setApprenticesFromGroup(mp)
          }}
        />

        <Steps
          className={'mt-4'}
          model={stepItems}
          activeIndex={showFeedback ? 1 : 0}
        />

        <Card className={'mt-2 mb-2 pt-2 p-2'} style={{ minHeight: '50vh' }}>
          {!showFeedback && (
            <div
              className={'flex flex-wrap gap-1 ml-0 pl-0 pt-0 overflow-auto'}
            >
              <div className={'flex col-12 mb-2 ml-0 pl-0 h-full'}>
                <DataTable
                  header={t<string>('grade_module.module.name')}
                  value={selectedModules}
                  rowHover
                  stripedRows
                  scrollable={true}
                  scrollHeight={'40vh'}
                  emptyMessage={t<string>('core.select-min-1')}
                  className={'col'}
                >
                  <Column
                    field={'module_key'}
                    header={t<string>('grade_module.module.key')}
                    sortable
                    style={{ maxWidth: '100px' }}
                  />
                  <Column
                    field={'description'}
                    header={t<string>('grade_module.module.description')}
                    sortable
                    bodyClassName={'small-text'}
                  />
                  <Column
                    field={'semester'}
                    header={'S'}
                    style={{ maxWidth: '40px' }}
                    sortable
                  />
                  <Column
                    field={'id_rounding_type'}
                    header={'RT'}
                    body={(e) => {
                      if (e.id_grade_schema === 5) {
                        return <></>
                      }
                      return (
                        <>
                          {t(
                            `grade_module.grade.rounding_type.${e.id_rounding_type}`
                          )}
                        </>
                      )
                    }}
                    sortable
                  />
                  <Column
                    field={'id_grade_schema'}
                    header={'Schema'}
                    className={'small-text justify-content-end'}
                    body={(e) =>
                      t<string>(
                        `grade_module.grade.schema.${e.id_grade_schema}`
                      )
                    }
                    sortable
                  />
                  <Column
                    body={actionBodyRemove(removeModule)}
                    style={{ maxWidth: '40px' }}
                  />
                </DataTable>

                <DataTable
                  header={t<string>('core.apprentices')}
                  value={selectedApprentices}
                  rowHover
                  stripedRows
                  scrollable={true}
                  scrollHeight={'40vh'}
                  dataKey={'person_id'}
                  emptyMessage={t<string>('core.select-min-1')}
                  className={'col'}
                >
                  <Column
                    field={'person_id'}
                    header={t<string>('core.apprentices')}
                    body={(a) => <AvatarWithName person={a} />}
                  />
                  <Column
                    field={'apprentice.id_profession'}
                    header={t<string>('apprentice.profession')}
                    sortable
                    body={ProfessionString}
                  />
                  <Column
                    field={'apprentice.id_speciality'}
                    header={t<string>('apprentice.speciality')}
                    body={SpecialityString}
                    sortable
                  />
                  <Column
                    body={actionBodyRemove(removeApprentice)}
                    style={{ maxWidth: '40px' }}
                  />
                </DataTable>
              </div>
            </div>
          )}

          {showFeedback && (
            <>
              <div>
                {existingModules.length > 0 || createdModules.length > 0 ? (
                  <Card
                    className={
                      'grid p-inline-message p-inline-message-success mt-2'
                    }
                  >
                    <span className={'flex small-text align-items-center'}>
                      {t<string>(
                        'grade_module.create_apprentice_grade_containers_successful'
                      )}
                    </span>
                  </Card>
                ) : (
                  <Card
                    className={
                      'grid p-inline-message p-inline-message-error mt-2'
                    }
                  >
                    <span className={'flex small-text align-items-center'}>
                      {t<string>(
                        'grade_module.create_apprentice_grade_containers_failed'
                      )}
                    </span>
                  </Card>
                )}
              </div>
              <div className={'flex w-full gap-2 mt-4'}>
                <Card className={'col mokai-gradient'}>
                  <b>
                    {t<string>('core.new_created')} ({createdModules.length})
                  </b>
                  <div
                    className={'w-full overflow-auto mt-2'}
                    style={{ height: '36vh' }}
                  >
                    {createdModules.map(
                      (mod: ApprenticeModuleGrade, index: number) => (
                        <Card key={'new_' + index} className={'w-full  mt-1'}>
                          <div className={'grid w-full align-items-center'}>
                            <div className={'col'}>
                              <AvatarWithName
                                person={getApprentice(mod.id_apprentice)}
                              />
                            </div>
                            <div className={'col-1'}>S{mod.semester}</div>
                            <div className={'col-2'}>
                              <b>
                                {
                                  selectedModules?.find(
                                    (mo) =>
                                      mo.apprenticeship_module_id ===
                                      mod.id_apprenticeship_module
                                  )?.module_key
                                }
                              </b>
                            </div>
                            <div className={'col small-text'}>
                              {
                                modulePlan?.apprenticeship_modules?.find(
                                  (mo) =>
                                    mo.apprenticeship_module_id ===
                                    mod.id_apprenticeship_module
                                )?.description
                              }
                            </div>
                          </div>
                        </Card>
                      )
                    )}
                  </div>
                </Card>
                <Card className={'col mokai-gradient'}>
                  <b>
                    {t<string>('core.already_existing')} (
                    {existingModules.length})
                  </b>
                  <div
                    className={'w-full overflow-auto mt-2'}
                    style={{ height: '36vh' }}
                  >
                    {existingModules.map(
                      (mod: ApprenticeModuleGrade, index: number) => (
                        <Card
                          key={'existing' + index}
                          className={'w-full mt-1'}
                        >
                          <div className={'grid w-full align-items-center'}>
                            <div className={'col'}>
                              <AvatarWithName
                                person={getApprentice(mod.id_apprentice)}
                              />
                            </div>
                            <div className={'col-1'}>S{mod.semester}</div>
                            <div className={'col-2'}>
                              <b>
                                {
                                  selectedModules?.find(
                                    (mo) =>
                                      mo.apprenticeship_module_id ===
                                      mod.id_apprenticeship_module
                                  )?.module_key
                                }
                              </b>
                            </div>
                            <div className={'col small-text'}>
                              {
                                modulePlan?.apprenticeship_modules?.find(
                                  (mo) =>
                                    mo.apprenticeship_module_id ===
                                    mod.id_apprenticeship_module
                                )?.description
                              }
                            </div>
                          </div>
                        </Card>
                      )
                    )}
                  </div>
                </Card>
              </div>
              {existingModules.length > 0 && (
                <Card
                  className={'grid p-inline-message p-inline-message-info mt-2'}
                >
                  <span className={'flex small-text align-items-center'}>
                    {t<string>(
                      'grade_module.create_apprentice_grade_containers_info'
                    )}
                  </span>
                </Card>
              )}
            </>
          )}
        </Card>
        <div
          className={
            'flex w-full justify-content-end align-items-end pr-2 gap-2'
          }
        >
          <Button
            label={
              showFeedback
                ? t<string>('grade_module.create_other_containers')
                : t<string>('core.reset')
            }
            className={'p-button-outlined'}
            onClick={() => {
              clearForm()
              setModulePlan({})
            }}
            type={'button'}
          />
          {showFeedback ? (
            <Button
              label={t<string>('navigation.go_to_grades_overview')}
              className={'p-button'}
              onClick={() => {
                navigate(AuthRoutes.grades)
              }}
              type={'button'}
            />
          ) : (
            <Button
              label={t<string>(
                'grade_module.create_apprentice_grade_containers'
              )}
              onClick={() => handleCreate()}
              type={'button'}
              disabled={
                selectedModules.length === 0 || selectedApprentices.length === 0
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}